/**
 * First-look walkthrough of the board.
 *
 * Onboarding (FR-1) gets a stranger from a blank page to a live board in under
 * a minute, but it never explains the board itself — three columns, a source
 * selector, and a More menu, dropped on someone the moment the last onboarding
 * step resolves. This fills that gap without adding a fourth onboarding step:
 * it opens once automatically the first time a board is reached, and is
 * reachable again from More any time after.
 *
 * Content lives here as plain steps rather than pointing at live board
 * elements, on purpose. A coach-mark tour that highlights "the left column"
 * would need a different script under 900px, where the columns are stacked
 * tabs instead — one text description that names both layouts is simpler and
 * cannot drift out of sync with either.
 */

import { el, clear } from './dom.ts';

interface TutorialStep {
  title: string;
  body: string[];
}

const STEPS: TutorialStep[] = [
  {
    title: 'Draft Assistant',
    body: [
      'Every player still available, ranked by whichever source is active up top — your own list, a single source, or the blend. This is the left column on a laptop, the "Board" tab on a phone.',
      'Tap a player for the full breakdown. The checkbox next to a name strikes them off the board the moment they’re drafted, by anyone.',
    ],
  },
  {
    title: 'Take next',
    body: [
      'The top five picks the engine would make right now, each with the actual reason underneath it — value, a roster need, a scarcity warning, or a bye-week conflict. Not just a number.',
      'This re-ranks live as the draft moves and as other people pick, not just on your turn.',
    ],
  },
  {
    title: 'Your roster',
    body: [
      'Filled slots, open starting needs, and your next few picks in the snake — so you know how many rounds you actually have before a thin position runs out.',
    ],
  },
  {
    title: 'Load your own rankings',
    body: [
      'More → "+ Rankings" pastes a list, uploads a CSV, or fetches a URL. Sleeper’s own order is a weak signal on its own — it clusters quarterbacks unrealistically high — so if you want "Just follow my list" to mean something, this is where you give it a real list to follow.',
    ],
  },
  {
    title: 'Where the numbers come from',
    body: [
      'Nothing here is a black box. View options → "? Numbers & colors" explains every column and every part of a recommendation score — including what the projections cannot tell you, and why.',
      'It describes your current settings rather than the defaults, so it always matches what you are actually looking at.',
    ],
  },
  {
    title: 'More menu',
    body: [
      '"Recommendation settings" tunes the weights live against your actual board — nothing here is fixed opinion.',
      '"Preflight check" is the one to run the night before, not during. "Printable cheat sheet" is the paper backup if the tool or your connection goes down mid-draft. "Replay a draft" rehearses the whole board against one that already finished.',
    ],
  },
  {
    title: 'Keyboard shortcuts',
    body: [
      '1–6 filters by position, 0 clears it, s switches the active ranking source, w opens weights, h explains the numbers, Esc closes whatever’s open. Press ? any time to see this list again — that shortcut still works after you close this.',
    ],
  },
];

export function renderTutorial(host: HTMLElement, onClose: () => void): void {
  clear(host);
  let index = 0;

  const body = el('div', { class: 'tutorial-body' });
  const dots = el('div', { class: 'tutorial-dots', 'aria-hidden': 'true' });
  const back = el('button', { class: 'btn btn-quiet', type: 'button' }, 'Back') as HTMLButtonElement;
  const next = el('button', { class: 'btn btn-primary', type: 'button' }) as HTMLButtonElement;

  const renderStep = () => {
    const step = STEPS[index]!;
    clear(body);
   body.appendChild(el('h3', {}, step.title));
    for (const paragraph of step.body) body.appendChild(el('p', {}, paragraph));

    clear(dots);
    STEPS.forEach((_, i) => dots.appendChild(el('span', { class: `tutorial-dot ${i === index ? 'active' : ''}` })));

    back.disabled = index === 0;
    next.textContent = index === STEPS.length - 1 ? 'Done' : 'Next';
  };

  back.addEventListener('click', () => {
    if (index > 0) {
      index -= 1;
      renderStep();
    }
  });
  next.addEventListener('click', () => {
    if (index < STEPS.length - 1) {
      index += 1;
      renderStep();
      return;
    }
    onClose();
  });

  host.appendChild(
    el(
      'div',
      { class: 'modal tutorial-modal', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'How Draft Puppy works' },
      el(
        'header',
        { class: 'modal-head' },
        el('h2', {}, 'How this works'),
        el('button', { class: 'btn btn-quiet', type: 'button', onClick: onClose, 'aria-label': 'Close' }, '×'),
      ),
      body,
      dots,
      el(
        'footer',
        { class: 'modal-foot' },
        el('button', { class: 'btn btn-quiet', type: 'button', onClick: onClose }, 'Skip'),
        back,
        next,
      ),
    ),
  );

  renderStep();
}
